import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import memoizeOne from 'memoize-one';
import { DeepReadonlyArray, DeepReadonlyObject } from 'rxdb';
import { map } from 'rxjs';
import invariant from 'tiny-invariant';

import { PRODT_ITINERARY_DEFAULT_DISCOUNT } from '@mopla/constants';
import {
	BookingDetailPaymentInfoView,
	CurrencyCodeEnum,
	DiscountStateEnum,
	ICheaperItineraryView,
	ItineraryView,
	LegsPlaceView,
	LegView,
	NamedLocationView,
	PassengerBookingView,
	PricingElementView,
	TransportIconKey,
	TripTypeEnum,
	VehicleType,
	WithNullableDiscountState,
} from '@mopla/data-models';

import { getCountdownTimer$ } from './useCountdownTimer$/useCountdownTimer$';
import { isMoreThanOneTrue, uniqueFilter } from './array';
import { formatDate, formatTime } from './formatDateTime';

export const checkIsLBODTFlexLeg = (leg: LegView): boolean =>
	leg.mode === TripTypeEnum.LBODTFLEX;

export const checkIsPRODTLeg = (leg: LegView): boolean =>
	leg.mode === TripTypeEnum.PRODT;

export const checkIsAnyWalkLeg = (
	leg: LegView | DeepReadonlyObject<LegView>
): boolean =>
	[TripTypeEnum.WALK, TripTypeEnum.TRANSFER_WALK].includes(leg.mode);

/** Exhaustive on purpose: a new trip type must be classified rather than inherit a default. */
const mapLegModeToStatusTrackable: Record<TripTypeEnum, boolean> = {
	[TripTypeEnum.PRODT]: true,
	[TripTypeEnum.LBODT]: true,
	[TripTypeEnum.LBODTFLEX]: true,
	[TripTypeEnum.SPECIAL_SCHOOL_TRAFFIC]: true,
	[TripTypeEnum.WALK]: false,
	[TripTypeEnum.TRANSFER_WALK]: false,
	[TripTypeEnum.LBT]: false,
	/** Retired on the BE; listed only because the generated types still carry them. */
	[TripTypeEnum.LBODTDS]: false,
	[TripTypeEnum.POOLODT]: false,
	[TripTypeEnum.OTHERS]: false,
	[TripTypeEnum.EMPTY]: false,
	[TripTypeEnum.BLOCKER]: false,
	[TripTypeEnum.UNCATEGORIZED]: false,
};

/** Whether the leg carries a legStatus of its own. */
export const checkHasLegStatus = (
	leg: LegView | DeepReadonlyObject<LegView>
): boolean => mapLegModeToStatusTrackable[leg.mode];

/** TODO pay attention how types of rides are checked
 * some of those fns use "every", which answers a question "is a ride *-type only?"
 * some of thise use "some", which is "has a ride *-type legs?" */
export const isLBTItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		return !!itinerary?.legs.every((leg) =>
			[
				TripTypeEnum.LBT,
				TripTypeEnum.WALK,
				TripTypeEnum.TRANSFER_WALK,
			].includes(leg.mode)
		);
	}
);

export const isLBODTFlexItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		return !!itinerary?.legs.some((leg) => leg.mode === TripTypeEnum.LBODTFLEX);
	}
);

export const isLBODTItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		return !!itinerary?.legs.some((leg) => leg.mode === TripTypeEnum.LBODT);
	}
);

export const isSpecialSchoolTrafficItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		return !!itinerary?.legs.some(
			(leg) => leg.mode === TripTypeEnum.SPECIAL_SCHOOL_TRAFFIC
		);
	}
);

/** At the moment, a PRODT ride is a ride with 1 PRODT leg and maybe a WALK legs at the start and end */
export const isPRODTItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		if (!itinerary) {
			return false;
		}

		const tt = getItineraryTransportTypes(itinerary, [TripTypeEnum.WALK]);

		return tt.length === 1 && tt[0].transportType === TripTypeEnum.PRODT;
	}
);

/** At the moment, a PRODT ride cannot be a part of a mixed ride */
export const isMixedItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		if (!itinerary) {
			return false;
		}

		const hasLBODTFlexLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBODTFLEX
		);

		const hasLBTLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBT
		);

		const hasLBODTLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBODT
		);

		return isMoreThanOneTrue([hasLBODTFlexLegs, hasLBTLegs, hasLBODTLegs]);
	}
);

/** LBT + LBODT combination with no LBODTFLEX legs — pure public transport mixed ride */
export const isMixedPublicTransportItinerary = memoizeOne(
	(itinerary?: DeepReadonlyObject<ItineraryView> | null) => {
		if (!itinerary) return false;

		const hasLBTLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBT
		);
		const hasLBODTLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBODT
		);
		const hasLBODTFlexLegs = itinerary.legs.some(
			(leg) => leg.mode === TripTypeEnum.LBODTFLEX
		);

		return hasLBTLegs && hasLBODTLegs && !hasLBODTFlexLegs;
	}
);

/** Returns unique Array of TransportTypes */
export const getItineraryTransportTypes = memoizeOne(
	(
		itinerary?: DeepReadonlyObject<ItineraryView> | ItineraryView | null,
		exclude?: TripTypeEnum[]
	): ITransportDescriptor[] => {
		const _exclude = exclude || [];
		return itinerary
			? itinerary.legs
					.map(
						(leg): ITransportDescriptor => ({
							transportType: leg.mode,
							vehicleType: leg.vehicleType,
							lbodtflexLabel: leg.lbodtflexLabel,
						})
					)
					.filter(
						(v, i, l) =>
							!_exclude.includes(v.transportType) &&
							_uniqueTransportDescriptorFilter(v, i, l)
					)
			: [];
	}
);

const _uniqueTransportDescriptorFilter = (
	value: ITransportDescriptor,
	index: number,
	self: ITransportDescriptor[]
): boolean => {
	return (
		self.findIndex(
			(v) =>
				v.transportType === value.transportType &&
				v.vehicleType === value.vehicleType
		) === index
	);
};

/** Checks if an itinerary has any leg with a start or end datetime, which is not fixed (thus can be changed by a transport provider) */
export const checkTimeMightChange = memoizeOne(
	(itinerary?: ItineraryView | null) => {
		if (!itinerary) {
			return false;
		}

		return itinerary.legs.some((leg: LegView) => {
			if (leg.timeInfo) {
				return leg.timeInfo.startAndEndTimeFixed !== true;
			}

			/** Fallback to old fields */
			return !leg.startDateTimeFix || !leg.endDateTimeFix;
		});
	}
);

/** Checks if a Booking price has been reduced */
export const checkBookingPriceReduced = memoizeOne(
	(bookedItinerary: PassengerBookingView) => {
		const newPrice = bookedItinerary.payment.overwrittenPaymentAmount;
		const initialPrice = bookedItinerary.payment.paymentAmount;
		return Boolean(newPrice && initialPrice && newPrice < initialPrice);
	}
);

/**
 * For the given discountType and the itinerary,
 * calculates the price value.
 * */
export const calcPriceByDiscountType = memoizeOne(
	(itinerary: ItineraryView, discountType: DiscountStateEnum) => {
		let totalAmount: number | null = null;
		let currency: CurrencyCodeEnum | null = null;

		itinerary.legs.forEach((leg) => {
			if (leg.pricings && leg.pricings.length > 0) {
				const suitablePricing = leg.pricings.find(
					(pricing) =>
						pricing.discount === discountType &&
						pricing.paymentInformationAvailable
				);

				if (!suitablePricing) {
					return;
				}

				/**
				 * Pay attention that this calculation won't include the `suitablePricing.deploymentPrice` part.
				 * The "deploymentPrice" contains non-0 value when the "Bereitstellungspreis pro Buchung" checkbox is set for the booking timeframe.
				 * This part should be added outside of this function, because we don't want to summ the deploymentPrice for all passengers, but rather to add it just once.
				 * This function doesn't know how many passengers are in the booking.
				 * */
				if (suitablePricing.paymentInformationAvailable) {
					totalAmount =
						(totalAmount || 0) + (suitablePricing.pricePerPassenger || 0);
					currency = suitablePricing.currency || null;
				}
			}
		});

		return {
			totalAmount,
			currency,
		};
	}
);

export function checkHasLegWithoutPaymentInfo(
	legs: DeepReadonlyArray<LegView>
) {
	const legsPrices = getLegsPrices(legs);

	return legsPrices.some((legPrices) =>
		legPrices.some(
			({ paymentInformationAvailable }) => !paymentInformationAvailable
		)
	);
}

export function getLegsPrices(
	legs: DeepReadonlyArray<LegView>
): DeepReadonlyArray<PricingElementView>[] {
	return legs.map(({ pricings }) => pricings);
}

export const getMoplaLegs = memoizeOne((legs: LegView[]) =>
	legs.filter((leg) =>
		[TripTypeEnum.PRODT, TripTypeEnum.LBODT, TripTypeEnum.LBODTFLEX].includes(
			leg.mode
		)
	)
);

/** Types are not ideal here */
export function prepareItineraryOperators<T>(
	itinerary?: ItineraryView | null,
	mapper?: (operators: string[]) => unknown
): null | T {
	if (!itinerary) {
		return null;
	}

	const operators = itinerary.legs
		.map((leg) => leg.agencyName)
		.filter((v, i, s): v is string => !!v && uniqueFilter(v, i, s));

	if (!operators.length) {
		return null;
	}

	return (mapper ? mapper(operators) : operators) as T;
}

type TPricingsRangeSource = DeepReadonlyObject<
	Pick<ItineraryView, 'legs' | 'bookableDiscountStates'>
>;

/** `bookableDiscountStates` drops switched-off LBODTFLEX discount slots (they still carry a price). */
export function calculateItineraryPricingsRange(
	itinerary: TPricingsRangeSource | null
): [number, number] | null {
	if (!itinerary) {
		return null;
	}
	const { legs, bookableDiscountStates } = itinerary;
	let totalMin: number | null = null;
	let totalMax: number | null = null;

	const isSlotBookable = (
		leg: DeepReadonlyObject<LegView>,
		p: DeepReadonlyObject<PricingElementView>
	) =>
		!bookableDiscountStates ||
		leg.mode !== TripTypeEnum.LBODTFLEX ||
		(!!p.discount && bookableDiscountStates.includes(p.discount));

	legs.forEach((leg) => {
		const prices = leg.pricings
			.filter((p) => p.paymentInformationAvailable && isSlotBookable(leg, p))
			.map<number>((p) => (p.pricePerPassenger || 0) + (p.deploymentPrice || 0))
			.sort((a, b) => a - b);

		if (prices.length) {
			totalMin = (totalMin || 0) + prices[0] || 0;
			totalMax = (totalMax || 0) + prices[prices.length - 1] || 0;
		}
	});

	if (totalMin == null || totalMax == null) {
		return null;
	}

	return [totalMin, totalMax];
}

export function getActualPrice(payment: BookingDetailPaymentInfoView): number {
	const newPrice = payment.overwrittenPaymentAmount;
	const initialPrice = payment.paymentAmount ?? 0;

	return newPrice || initialPrice;
}

/** @deprecated
 * Let's used for a short period of time until the BE finishes adding the `timeInfo` field
 * */
export function getActualTime(leg: LegView, type: 'start' | 'end') {
	const newTime =
		type === 'start'
			? leg.overwrittenStartTimeWindowStart
			: leg.overwrittenEndTimeWindowEnd;

	const initialTime = type === 'start' ? leg.startDateTime : leg.endDateTime;
	return new Date(newTime || initialTime);
}

/**
 * Returns the effective start or end time of a leg, depending on the `type` argument.
 *
 * The `startTime` and `endTime` fields may contain outdated values, as they can be
 * overwritten by updated predictions (`currentlyPredictedStartTime` / `currentlyPredictedEndTime`).
 *
 * This function ensures the returned time is up-to-date ("effective") value.
 */
export function getEffectiveTimeV2(leg: LegView, type: 'start' | 'end') {
	let result;
	const start = leg.timeInfo?.start;
	const end = leg.timeInfo?.end;

	invariant(start && end, '');

	if (type === 'start') {
		if (start.currentlyPredictedStartTime) {
			result = start.currentlyPredictedStartTime;
		} else {
			result = start.startTime;
		}
	} else if (type === 'end') {
		if (end.currentlyPredictedEndTime) {
			result = end.currentlyPredictedEndTime;
		} else {
			result = end.endTime;
		}
	}

	invariant(result, 'Unexpected behavior');

	return new Date(result);
}

/** TODO This won't be needed when the BE fix the LegView and make `timeInfo` required */
export class LegTimeInfoAdapter {
	constructor(private leg: LegView) {}

	private getEffectiveTime(type: 'start' | 'end'): Date {
		if (this.leg.timeInfo) {
			return getEffectiveTimeV2(this.leg, type);
		} else {
			return getActualTime(this.leg, type);
		}
	}

	get effectiveStartTime(): Date {
		return this.getEffectiveTime('start');
	}

	get effectiveEndTime(): Date {
		return this.getEffectiveTime('end');
	}

	get startTime(): string {
		return this.leg.timeInfo?.start
			? this.leg.timeInfo.start.startTime
			: this.leg.startDateTime;
	}

	get endTime(): string {
		return this.leg.timeInfo?.end
			? this.leg.timeInfo.end.endTime
			: this.leg.endDateTime;
	}

	get startTimeForCancellation(): string {
		return (
			this.leg.timeInfo?.start?.startTimeForCancellation ||
			/** Those 3 are fallbacks for the backwards compatibility. Their usage is actually wrong */
			this.leg.timeInfo?.start?.currentlyPredictedStartTime ||
			this.leg.overwrittenStartTimeWindowStart ||
			this.leg.startDateTime
		);
	}
}

const getLegDistance = memoizeOne((leg: LegView): number => {
	return Math.round(Number(leg.distance || 0) / 100) / 10;
});

export const getLegDuration = memoizeOne(
	(leg: DeepReadonlyObject<LegView>): number => {
		return Math.round(Number(leg.duration || 0) / 60);
	}
);

export const getItineraryLegsTimeInfoAdapters = (
	legs: DeepReadonlyObject<LegView | LegView[]>
) => {
	const legsArray = Array.isArray(legs) ? legs : [legs];

	const startLeg = legsArray[0];
	const endLeg = legsArray[legsArray.length - 1];

	const startTimeInfoAdapter = new LegTimeInfoAdapter(startLeg);
	const endTimeInfoAdapter = new LegTimeInfoAdapter(endLeg);

	return { startLeg, startTimeInfoAdapter, endLeg, endTimeInfoAdapter };
};

export enum EItineraryTimeChange {
	/** A delayed-flagged leg with a known re-predicted time */
	DELAY = 'DELAY',
	/** Time moved earlier (manual operator change) */
	EARLIER = 'EARLIER',
	/** A leg is flagged delayed, but no re-predicted time was communicated (amount unknown) */
	INDETERMINATE_DELAY = 'INDETERMINATE_DELAY',
	/** Time moved later without the delayed flag (not significant per distributor config) */
	MINOR_DELAY = 'MINOR_DELAY',
	NONE = 'NONE',
}

export type TItineraryTimeChange = {
	type: EItineraryTimeChange;
	/** Whole minutes between the original and the effective departure (fallback: arrival). 0 when unknown. */
	diffMinutes: number;
	/** False when only the arrival moved — the departure texts then don't apply. */
	isDepartureChanged: boolean;
};

/** Truncated first, so 10:00:45 → 10:01:30 counts as the minute it shows. */
export const diffInShownMinutes = (
	from: dayjs.ConfigType,
	to: dayjs.ConfigType
): number =>
	dayjs(to).startOf('minute').diff(dayjs(from).startOf('minute'), 'minute');

/**
 * Classifies the change between the original and effective times. A delayed leg
 * counts as significant (`hasDelayedLeg`, configured per distributor); one with
 * no re-predicted time is a delay of unknown length. The departure diff wins
 * over the arrival diff, and `isDepartureChanged` says which one was used.
 */
export const calculateItineraryTimeChange = (
	hasDelayedLeg: boolean,
	startDateObj: dayjs.Dayjs,
	effectiveStartDateObj: Date,
	endDateObj: dayjs.Dayjs,
	effectiveEndDateObj: Date
): TItineraryTimeChange => {
	const startDiffMinutes = diffInShownMinutes(
		startDateObj,
		effectiveStartDateObj
	);
	const endDiffMinutes = diffInShownMinutes(endDateObj, effectiveEndDateObj);
	const isDepartureChanged = startDiffMinutes !== 0;
	const diffMinutes = isDepartureChanged ? startDiffMinutes : endDiffMinutes;

	if (diffMinutes < 0) {
		return {
			type: EItineraryTimeChange.EARLIER,
			diffMinutes,
			isDepartureChanged,
		};
	}

	if (diffMinutes > 0) {
		return {
			type: hasDelayedLeg
				? EItineraryTimeChange.DELAY
				: EItineraryTimeChange.MINOR_DELAY,
			diffMinutes,
			isDepartureChanged,
		};
	}

	if (hasDelayedLeg) {
		return {
			type: EItineraryTimeChange.INDETERMINATE_DELAY,
			diffMinutes: 0,
			isDepartureChanged,
		};
	}

	return {
		type: EItineraryTimeChange.NONE,
		diffMinutes: 0,
		isDepartureChanged,
	};
};

export const getItineraryTimingV2 = memoizeOne(
	(legs: DeepReadonlyObject<LegView | LegView[]>) => {
		const legsArray = Array.isArray(legs) ? legs : [legs];

		const { startLeg, startTimeInfoAdapter, endLeg, endTimeInfoAdapter } =
			getItineraryLegsTimeInfoAdapters(legs);

		const startDateObj = dayjs(startTimeInfoAdapter.startTime);
		const startDateFormatted = formatDate(startTimeInfoAdapter.startTime);
		const startTimeFormatted = formatTime(startTimeInfoAdapter.startTime);
		const effectiveStartDateObj = startTimeInfoAdapter.effectiveStartTime;
		const effectiveStartTimeFormatted = formatTime(effectiveStartDateObj);
		const isStartTimeChanged = !startDateObj.isSame(
			effectiveStartDateObj,
			'minute'
		);

		const endDateObj = dayjs(endTimeInfoAdapter.endTime);
		const endTimeFormatted = formatTime(endTimeInfoAdapter.endTime);
		const effectiveEndDateObj = endTimeInfoAdapter.effectiveEndTime;
		const effectiveEndTimeFormatted = formatTime(effectiveEndDateObj);
		const isEndTimeChanged = !endDateObj.isSame(effectiveEndDateObj, 'minute');

		const hasDelayedLeg = legsArray.some((leg) => Boolean(leg.delayed));

		/** TODO check this with real data */
		const isShifted =
			!hasDelayedLeg && (isStartTimeChanged || isEndTimeChanged);
		const isDelayed = hasDelayedLeg && (isStartTimeChanged || isEndTimeChanged);

		/** The richer variant of isDelayed/isShifted — see {@link calculateItineraryTimeChange}. */
		const timeChange = calculateItineraryTimeChange(
			hasDelayedLeg,
			startDateObj,
			effectiveStartDateObj,
			endDateObj,
			effectiveEndDateObj
		);

		const duration = dayjs.duration(
			dayjs(effectiveEndDateObj).diff(dayjs(effectiveStartDateObj))
		);

		const minutesBeforeStart$ = getCountdownTimer$(effectiveStartDateObj).pipe(
			map((duration) => duration.asMinutes())
		);

		let walkBefore, walkAfter;

		if (startLeg.mode === TripTypeEnum.WALK) {
			walkBefore = {
				distance: getLegDistance(startLeg),
				duration: getLegDuration(startLeg),
				effectiveStartDateObj: startTimeInfoAdapter.effectiveStartTime,
			};
		}

		if (endLeg.mode === TripTypeEnum.WALK) {
			walkAfter = {
				distance: getLegDistance(endLeg),
				duration: getLegDuration(endLeg),
				effectiveStartDateObj: endTimeInfoAdapter.effectiveEndTime,
			};
		}

		return {
			walkBefore,
			walkAfter,

			startLeg,
			startTime: startTimeFormatted,
			startDateObj,
			effectiveStartDateObj,
			effectiveStartTime: effectiveStartTimeFormatted,
			isStartTimeChanged,
			startDate: startDateFormatted,

			endLeg,
			endTime: endTimeFormatted,
			endDateObj,
			effectiveEndDateObj,
			effectiveEndTime: effectiveEndTimeFormatted,
			isEndTimeChanged,

			duration,
			isDelayed,
			isShifted,
			timeChange,

			minutesBeforeStart$,
		};
	}
);

export type TItineraryTiming = ReturnType<typeof getItineraryTimingV2>;

export function prepareCancellationData<T extends WithNullableDiscountState>(
	itinerary: ItineraryView,
	passengersList?: T[]
) {
	/**
	 *  The passengersList can be omitted for PRODT itineraries,
	 *  as the PRODT type charges a single price with NO_DISCOUNT for the entire vehicle rather than per passenger.
	 * */
	if (isPRODTItinerary(itinerary)) {
		passengersList = [
			{ discountState: PRODT_ITINERARY_DEFAULT_DISCOUNT },
		] as T[];
	}

	const cancellationSummary: { [key: number]: number } = {};

	itinerary.legs.forEach((leg: LegView) => {
		invariant(
			passengersList,
			'[ItineraryView] no passengersList while prepareCancellationData'
		);

		passengersList.forEach((passenger) => {
			const applicableRules = leg.cancellationRules?.filter(
				(rule) => rule.discount === passenger.discountState
			);

			applicableRules?.forEach((rule) => {
				invariant(
					Number.isFinite(rule.secondsBeforeRideStart),
					`[ItineraryView] cancellationRules secondsBeforeRideStart is not defined: ${rule.secondsBeforeRideStart}`
				);

				cancellationSummary[rule.secondsBeforeRideStart] =
					(cancellationSummary[rule.secondsBeforeRideStart] || 0) + rule.cost;
			});
		});
	});

	return Object.entries(cancellationSummary)
		.map(([secondsBeforeRideStart, cost]) => ({
			secondsBeforeRideStart: Number(secondsBeforeRideStart),
			cost,
		}))
		.sort((a, b) => b.secondsBeforeRideStart - a.secondsBeforeRideStart);
}

/**
 * Determines if an itinerary qualifies as a long delay with free cancellation.
 * Returns true if the delay exceeds 45 minutes and qualifies for free cancellation, otherwise false.
 * Only the PRODT rides are eligible for the free cancellation
 */
export function checkIsLongDelayFreeCancellation(itinerary: ItineraryView) {
	const isPRODT = isPRODTItinerary(itinerary);

	if (!isPRODT) {
		return false;
	}

	const {
		isDelayed,
		effectiveStartDateObj,
		effectiveEndDateObj,
		startDateObj,
		endDateObj,
		endLeg,
	} = getItineraryTimingV2(itinerary.legs);

	const departureTimeShift = dayjs(effectiveStartDateObj).diff(
		dayjs(startDateObj),
		'minutes'
	);

	const arrivalTimeShift = dayjs(effectiveEndDateObj).diff(
		dayjs(endDateObj),
		'minutes'
	);

	const maxTimeShift = Math.max(departureTimeShift, arrivalTimeShift);

	return (
		endLeg.maxDelayBeforeFreeCancellation &&
		isDelayed &&
		maxTimeShift > endLeg.maxDelayBeforeFreeCancellation
	);
}

export function findCancellationFee(itinerary: ItineraryView): number {
	const isLongDelayFreeCancellation =
		checkIsLongDelayFreeCancellation(itinerary);

	if (isLongDelayFreeCancellation) {
		return 0;
	}

	return itinerary.legs.reduce<number>((accum, leg) => {
		if (!leg.cancellationRules) {
			return accum;
		}

		// descending order
		const legCancellationRulesSorted = [...leg.cancellationRules].sort(
			(a, b) => b.secondsBeforeRideStart - a.secondsBeforeRideStart
		);

		if (!legCancellationRulesSorted) {
			return accum;
		}

		const legTimeViewAdapter = new LegTimeInfoAdapter(leg);

		/** We use a special field from the BE, which contains the correct datetime to use when calculating the fee */
		const startTimeForCancellation =
			legTimeViewAdapter.startTimeForCancellation;

		const actualSecondsBeforeLegStart = Math.max(
			0,
			dayjs(startTimeForCancellation).diff(dayjs(), 'seconds')
		);

		const matchedFee = legCancellationRulesSorted.find(
			(rule) => actualSecondsBeforeLegStart >= rule.secondsBeforeRideStart
		);

		return accum + (matchedFee ? matchedFee.cost : 0);
	}, 0);
}

export function calculateTotalPastCancellationPrice(itinerary: ItineraryView) {
	return itinerary.legs.reduce(
		(acc, currentLeg) => acc + (currentLeg.costOfCancellation || 0),
		0
	);
}

export function prepareLegAddress(
	legLocation?: NamedLocationView | LegsPlaceView
): string {
	if (!legLocation) {
		return '';
	}

	let prepared = legLocation.name;

	if (legLocation.stopDescription) {
		prepared += `\n${legLocation.stopDescription}`;
	}

	return prepared;
}

export const checkIsCheaperItinerarySuggestion = (
	itinerary: ItineraryView | ICheaperItineraryView
): itinerary is ICheaperItineraryView => {
	return Boolean(
		'isCheaperSuggestion' in itinerary && itinerary.isCheaperSuggestion
	);
};

/**
 * Maps a low-level {@link TripTypeEnum} mode to a compact UI icon key.
 *
 * Legend:
 * - 'K' — on-demand car (LBODTFLEX, LBODT)
 * - 'R' — scheduled bus (LBT)
 * - 'WALK' — walking
 * - 'OTHER' — any unrecognized/unsupported mode
 *
 * @param {TripTypeEnum} mode - The raw leg mode.
 * @returns {TransportIconKey} The icon category key used for rendering.
 * @example
 * modeToIconKey(TripTypeEnum.LBODTFLEX) // 'K'
 */
export const modeToIconKey = (mode: TripTypeEnum): TransportIconKey => {
	switch (mode) {
		case TripTypeEnum.LBODTFLEX:
		case TripTypeEnum.LBODT:
			return 'K';
		case TripTypeEnum.LBT:
			return 'R';
		case TripTypeEnum.WALK:
			return 'WALK';
		default:
			return 'OTHER';
	}
};

/**
 * Returns the itinerary's transport **modes** in original order, excluding given modes,
 * while collapsing only **adjacent** duplicates by *icon category* (as defined by {@link modeToIconKey}).
 *
 * Use this for the icon row so sequences like K-R-K are preserved, while K-K-R collapses to K-R.
 *
 * Notes:
 * - `exclude` is matched against raw {@link TripTypeEnum} values (e.g., `TRANSFER_WALK`).
 * - This function is memoized with `memoizeOne`; for best reuse, pass a stable `exclude` array.
 *
 * @param {ItineraryView | null | undefined} itinerary - The itinerary to extract modes from.
 * @param {TripTypeEnum[]} [exclude=[]] - Raw modes to exclude entirely from the output.
 * @returns {ITransportDescriptor[]} Ordered modes with consecutive duplicates (by icon group) removed.
 *
 * @example
 * // legs: [LBODTFLEX, LBT, LBODTFLEX]
 * getItineraryTransportTypesOrdered(itin, [TripTypeEnum.TRANSFER_WALK])
 * // → [LBODTFLEX, LBT, LBODTFLEX]   (KRK preserved)
 *
 * @example
 * // legs: [LBODTFLEX, LBODT, LBT]  // LBODTFLEX & LBODT both map to 'K'
 * getItineraryTransportTypesOrdered(itin)
 * // → [LBODTFLEX, LBT]             (K,K,R → K,R)
 */

export const getItineraryTransportTypesOrdered = memoizeOne(
	(
		itinerary?: ItineraryView | null,
		exclude: TripTypeEnum[] = []
	): ITransportDescriptor[] => {
		if (!itinerary) {
			return [];
		}

		const out: ITransportDescriptor[] = [];

		let lastIcon: TransportIconKey | undefined;

		for (const leg of itinerary.legs) {
			const { mode, vehicleType } = leg;

			if (exclude.includes(mode)) continue;

			const icon = modeToIconKey(mode);
			// collapse only consecutive duplicates by icon group (K/R/WALK/OTHER)
			if (out.length === 0 || icon !== lastIcon) {
				// keep the first mode of the consecutive block (e.g., LBODTFLEX over LBODT)
				out.push({
					transportType: mode,
					vehicleType,
				});
				lastIcon = icon;
			}
		}

		return out;
	}
);

export interface ITransportDescriptor {
	transportType: TripTypeEnum;
	vehicleType?: VehicleType;
	lbodtflexLabel?: string;
}

// Filters out all walking legs and returns the first remaining transport type.
// Since this is only meant to give an idea of the transport type, it does not matter if there are multiple transport types in the itinerary.
export const getTripTypeForGoogleAnalytics = (
	itinerary: ItineraryView
): TripTypeEnum => {
	const legsWithoutWalk = itinerary.legs.filter(
		(leg) => leg.mode !== TripTypeEnum.WALK
	);
	return legsWithoutWalk[0].mode;
};

export const getTransportTypesText = (
	transport: ITransportDescriptor[],
	t: TFunction
): string => {
	const filtered = transport
		.filter(({ transportType }) => transportType !== TripTypeEnum.WALK)
		.map(({ transportType, lbodtflexLabel }) => {
			if (transportType === TripTypeEnum.LBODTFLEX) {
				return lbodtflexLabel
					? t('booking:travel_types.LBODTFLEX.title_flex', { lbodtflexLabel })
					: t('booking:travel_types.LBODTFLEX.title_default');
			}

			return t(`booking:travel_types.${transportType}.title`);
		});

	return filtered[0];
};

export const getTimeStr = (
	itinerary: ItineraryView,
	transport: ITransportDescriptor[],
	t: TFunction
): string => {
	const { effectiveStartTime, effectiveEndTime } = getItineraryTimingV2(
		itinerary.legs
	);

	const transportTypes = transport.map(({ transportType }) => transportType);

	return `${
		transportTypes.includes(TripTypeEnum.LBODTFLEX)
			? t('common:text.around')
			: ''
	} ${effectiveStartTime} - ${effectiveEndTime}`;
};

export enum ERideType {
	/** MyWay - Private ride */
	PRODT = 'PRODT',
	/** Anrufbus Flex - On-demand flexible */
	LBODTFLEX = 'LBODTFLEX',
	/** Kombinierte Fahrt - Combined ride (includes LBODTFLEX) */
	MIXED = 'MIXED',
	/** Kombinierte Fahrt - Public transport only (LBT + LBODT, no LBODTFLEX) */
	MIXED_PUBLIC = 'MIXED_PUBLIC',
	/** Rufbus - On-demand bus */
	LBODT = 'LBODT',
	/** Linienbus - Regular bus line */
	LBT = 'LBT',
	/** RMBLS - single-route school traffic */
	SPECIAL_SCHOOL_TRAFFIC = 'SPECIAL_SCHOOL_TRAFFIC',
}

export function getItineraryRideType(
	itinerary: DeepReadonlyObject<ItineraryView>
): ERideType;
export function getItineraryRideType(
	itinerary: DeepReadonlyObject<ItineraryView> | null
): ERideType | null;
export function getItineraryRideType(
	itinerary: DeepReadonlyObject<ItineraryView> | null
): ERideType | null {
	if (!itinerary) return null;
	if (isSpecialSchoolTrafficItinerary(itinerary))
		return ERideType.SPECIAL_SCHOOL_TRAFFIC;
	if (isPRODTItinerary(itinerary)) return ERideType.PRODT;
	if (isMixedPublicTransportItinerary(itinerary)) return ERideType.MIXED_PUBLIC;
	if (isMixedItinerary(itinerary)) return ERideType.MIXED;
	if (isLBODTFlexItinerary(itinerary)) return ERideType.LBODTFLEX;
	if (isLBODTItinerary(itinerary)) return ERideType.LBODT;

	return ERideType.LBT;
}
